// frontend/src/components/WearablesView.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { BACKEND_URL } from "../config";
import { Capacitor, registerPlugin } from "@capacitor/core";

// ✅ MUST match: @CapacitorPlugin(name = "HealthSync")
const HealthSync = registerPlugin("HealthSync");

function WearablesView({ token, userId, showToast }) {
  const [wearableStatus, setWearableStatus] = useState(null);
  const [syncedData, setSyncedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = (message, type = "info") => showToast?.(message, type);

  // 'fitbit' | 'fitbit-history' | 'oura' | 'oura-history' | 'withings' | 'withings-history' | 'mobile' | 'garmin-import' | 'mfp-import' | null
  const [syncing, setSyncing] = useState(null);

  const [importing, setImporting] = useState(false);
  const [importSource, setImportSource] = useState("apple_watch");
  const [dateRange, setDateRange] = useState(7);

  // ✅ Mobile Health
  const [mobileSyncDays, setMobileSyncDays] = useState(14);
  const [autoMobileSyncEnabled, setAutoMobileSyncEnabled] = useState(false);

  // ✅ Withings
  const [withingsSyncDays, setWithingsSyncDays] = useState(30);

  const fileInputRef = useRef(null);
  const garminFileRef = useRef(null);
  const mfpFileRef = useRef(null);

  // ------------------- loaders -------------------
  const loadWearableStatus = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/wearables/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setWearableStatus(data);
      }
    } catch (error) {
      console.error("Error loading wearable status:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadSyncedData = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/wearables/synced-data?days=${dateRange}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setSyncedData(data.synced_data || []);
      }
    } catch (error) {
      console.error("Error loading synced data:", error);
    }
  }, [token, dateRange]);

  useEffect(() => {
    if (token) loadWearableStatus();
  }, [token, loadWearableStatus]);

  useEffect(() => {
    if (token) loadSyncedData();
  }, [token, dateRange, loadSyncedData]);

  // ✅ Detect OAuth returns and refresh status automatically (Fitbit/Oura/Withings)
  useEffect(() => {
    if (!token) return;

    const params = new URLSearchParams(window.location.search);

    const fitbitFlag = params.get("fitbit");
    const ouraFlag = params.get("oura");
    const withingsFlag = params.get("withings");
    const myfitnesspalFlag = params.get("myfitnesspal");

    const device = params.get("device");
    const status = params.get("status");
    const oauth = params.get("oauth");

    const isReturn =
      fitbitFlag === "connected" ||
      ouraFlag === "connected" ||
      withingsFlag === "connected" ||
      myfitnesspalFlag === "connected" ||
      (device && status === "connected") ||
      oauth === "success";

    if (isReturn) {
      (async () => {
        await loadWearableStatus();
        await loadSyncedData();
        showToast?.("Device connected successfully.", "success");
      })();

      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [token, loadWearableStatus, loadSyncedData, showToast]);

  // ✅ OPTIONAL: mobile auto-sync while app is open
  useEffect(() => {
    if (!autoMobileSyncEnabled) return;
    if (!token) return;
    if (!Capacitor.isNativePlatform()) return;

    const intervalMs = 6 * 60 * 60 * 1000;
    const id = setInterval(() => {
      if (syncing) return;
      syncMobileHealth(mobileSyncDays);
    }, intervalMs);

    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoMobileSyncEnabled, token, mobileSyncDays, syncing]);

  // ---------------- Autosync toggles (Fitbit/Oura/Withings) ----------------
  const setAutoSync = useCallback(
    async (provider, enabled) => {
      if (!token) return;
      try {
        const res = await fetch(`${BACKEND_URL}/api/wearables/autosync`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ provider, enabled }),
        });

        if (!res.ok) {
          const txt = await res.text();
          throw new Error(txt || "Failed to update autosync");
        }

        setWearableStatus((prev) => ({
          ...(prev || {}),
          [`auto_sync_${provider}`]: enabled,
        }));
        showToast?.(`${provider} autosync ${enabled ? "enabled" : "disabled"}.`, "success");
      } catch (e) {
        showToast?.(`Autosync update failed: ${e?.message || String(e)}`, "error");
      }
    },
    [token, showToast]
  );

  // ---------------- Fitbit ----------------
  const connectFitbit = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/wearables/fitbit/authorize`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.authorization_url;
      } else {
        const error = await response.json().catch(() => ({}));
        toast(error.detail || "Failed to initiate Fitbit connection", "error");
      }
    } catch (error) {
      toast(`Error connecting to Fitbit: ${error?.message || String(error)}`, "error");
    }
  };

  const disconnectFitbit = async () => {
    if (!window.confirm("Are you sure you want to disconnect Fitbit?")) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/wearables/fitbit/disconnect`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setWearableStatus((prev) => ({ ...(prev || {}), fitbit_connected: false, fitbit_user_id: null }));
        toast("Fitbit disconnected successfully", "success");
        await loadWearableStatus();
      } else {
        const txt = await response.text();
        toast(txt || "Failed to disconnect Fitbit", "error");
      }
    } catch (error) {
      toast(`Error disconnecting Fitbit: ${error?.message || String(error)}`, "error");
    }
  };

  const syncFitbitData = async () => {
    setSyncing("fitbit");
    try {
      const response = await fetch(`${BACKEND_URL}/api/wearables/fitbit/sync?date=today`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        toast(`Synced! Steps: ${data.data.steps || 0}, Calories: ${data.data.calories_burned || 0}`, "success");
        await loadSyncedData();
        await loadWearableStatus();
      } else {
        const error = await response.json().catch(() => ({}));
        toast(error.detail || "Sync failed", "error");
      }
    } catch (error) {
      toast(`Error syncing: ${error?.message || String(error)}`, "error");
    } finally {
      setSyncing(null);
    }
  };

  const syncFitbitHistory = async (days = 30) => {
    setSyncing("fitbit-history");
    try {
      const response = await fetch(`${BACKEND_URL}/api/wearables/fitbit/sync-history?days=${days}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        toast(`Successfully synced ${data.days_synced} days of historical data!`, "success");
        await loadSyncedData();
        await loadWearableStatus();
      } else {
        const error = await response.json().catch(() => ({}));
        toast(error.detail || "History sync failed", "error");
      }
    } catch (error) {
      toast(`Error syncing history: ${error?.message || String(error)}`, "error");
    } finally {
      setSyncing(null);
    }
  };

  // ---------------- Oura ----------------
  const connectOura = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/wearables/oura/authorize`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.authorization_url;
      } else {
        const error = await response.json().catch(() => ({}));
        toast(error.detail || "Failed to initiate Oura Ring connection", "error");
      }
    } catch (error) {
      toast(`Error connecting to Oura Ring: ${error?.message || String(error)}`, "error");
    }
  };

  const disconnectOura = async () => {
    if (!window.confirm("Are you sure you want to disconnect Oura Ring?")) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/wearables/oura/disconnect`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setWearableStatus((prev) => ({ ...(prev || {}), oura_connected: false }));
        toast("Oura Ring disconnected successfully", "success");
        await loadWearableStatus();
      } else {
        const txt = await response.text();
        toast(txt || "Failed to disconnect Oura Ring", "error");
      }
    } catch (error) {
      toast(`Error disconnecting Oura Ring: ${error?.message || String(error)}`, "error");
    }
  };

  const syncOuraData = async () => {
    setSyncing("oura");
    try {
      const response = await fetch(`${BACKEND_URL}/api/wearables/oura/sync?date=today`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const sleepHours = data.data.sleep_minutes ? Math.round(data.data.sleep_minutes / 60) : 0;
        toast(
          `Synced! Sleep: ${sleepHours}h, Readiness: ${data.data.readiness_score || "-"}, Activity: ${
            data.data.activity_score || "-"
          }`,
          "success"
        );
        await loadSyncedData();
        await loadWearableStatus();
      } else {
        const error = await response.json().catch(() => ({}));
        toast(error.detail || "Sync failed", "error");
      }
    } catch (error) {
      toast(`Error syncing: ${error?.message || String(error)}`, "error");
    } finally {
      setSyncing(null);
    }
  };

  const syncOuraHistory = async (days = 30) => {
    setSyncing("oura-history");
    try {
      const response = await fetch(`${BACKEND_URL}/api/wearables/oura/sync-history?days=${days}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        toast(`Successfully synced ${data.days_synced} days of historical data!`, "success");
        await loadSyncedData();
        await loadWearableStatus();
      } else {
        const error = await response.json().catch(() => ({}));
        toast(error.detail || "History sync failed", "error");
      }
    } catch (error) {
      toast(`Error syncing history: ${error?.message || String(error)}`, "error");
    } finally {
      setSyncing(null);
    }
  };

  // ---------------- Withings ----------------
  const connectWithings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/withings/authorize`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.authorization_url;
      } else {
        const txt = await response.text();
        toast(txt || "Failed to initiate Withings connection", "error");
      }
    } catch (error) {
      toast(`Error connecting to Withings: ${error?.message || String(error)}`, "error");
    }
  };

  const disconnectWithings = async () => {
    if (!window.confirm("Are you sure you want to disconnect Withings?")) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/withings/disconnect`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        setWearableStatus((prev) => ({ ...(prev || {}), withings_connected: false }));
        toast("Withings disconnected successfully", "success");
        await loadWearableStatus();
      } else {
        const txt = await response.text();
        toast(txt || "Failed to disconnect Withings", "error");
      }
    } catch (error) {
      toast(`Error disconnecting Withings: ${error?.message || String(error)}`, "error");
    }
  };

  const syncWithings = async (days = 30) => {
    setSyncing(days <= 1 ? "withings" : "withings-history");
    try {
      const response = await fetch(`${BACKEND_URL}/api/withings/sync?days=${days}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const txt = await response.text();
        throw new Error(txt || "Withings sync failed");
      }

      const data = await response.json();
      toast(`Withings sync complete: ${data.days_upserted ?? 0} record(s) upserted`, "success");
      await loadWearableStatus();
      await loadSyncedData();
    } catch (error) {
      toast(`Error syncing Withings: ${error?.message || String(error)}`, "error");
    } finally {
      setSyncing(null);
    }
  };

  // ---------------- Garmin (manual + CSV) ----------------
  const connectGarminManual = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/wearables/garmin/connect-manual`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to connect Garmin");
      }
      await loadWearableStatus();
      toast("Garmin connected (manual mode). Upload a CSV export to import data.", "success");
    } catch (e) {
      toast(`Error connecting Garmin: ${e?.message || String(e)}`, "error");
    }
  };

  const disconnectGarmin = async () => {
    if (!window.confirm("Are you sure you want to disconnect Garmin?")) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/wearables/garmin/disconnect`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to disconnect Garmin");
      }
      await loadWearableStatus();
      toast("Garmin disconnected.", "success");
    } catch (e) {
      toast(`Error disconnecting Garmin: ${e?.message || String(e)}`, "error");
    }
  };

  const importGarminCSV = async (file) => {
    if (!file) return;
    setSyncing("garmin-import");
    try {
      const form = new FormData();
      form.append("file", file);

      const res = await fetch(`${BACKEND_URL}/api/wearables/garmin/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Garmin import failed");

      toast(`Garmin import complete: ${data.imported_count || 0} day(s)`, "success");
      await loadSyncedData();
      await loadWearableStatus();
    } catch (e) {
      toast(`Error importing Garmin CSV: ${e?.message || String(e)}`, "error");
    } finally {
      setSyncing(null);
      if (garminFileRef.current) garminFileRef.current.value = "";
    }
  };

  // ---------------- MyFitnessPal (OAuth + manual CSV fallback) ----------------
  const connectMfpOAuth = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/wearables/myfitnesspal/authorize`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Failed to initiate MyFitnessPal connection");
      if (!data?.authorization_url) throw new Error("Missing MyFitnessPal authorization URL");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast(`Error connecting MyFitnessPal: ${e?.message || String(e)}`, "error");
    }
  };

  const connectMfpManual = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/wearables/myfitnesspal/connect-manual`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to connect MyFitnessPal");
      }
      await loadWearableStatus();
      toast("MyFitnessPal connected (manual mode). Upload a CSV export to import nutrition.", "success");
    } catch (e) {
      toast(`Error connecting MyFitnessPal: ${e?.message || String(e)}`, "error");
    }
  };

  const connectMfp = async () => {
    if (wearableStatus?.myfitnesspal_oauth_ready) {
      await connectMfpOAuth();
      return;
    }
    await connectMfpManual();
  };

  const disconnectMfp = async () => {
    if (!window.confirm("Are you sure you want to disconnect MyFitnessPal?")) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/wearables/myfitnesspal/disconnect`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to disconnect MyFitnessPal");
      }
      await loadWearableStatus();
      toast("MyFitnessPal disconnected.", "success");
    } catch (e) {
      toast(`Error disconnecting MyFitnessPal: ${e?.message || String(e)}`, "error");
    }
  };

  const importMfpCSV = async (file) => {
    if (!file) return;
    setSyncing("mfp-import");
    try {
      const form = new FormData();
      form.append("file", file);

      const res = await fetch(`${BACKEND_URL}/api/wearables/myfitnesspal/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "MyFitnessPal import failed");

      toast(`MyFitnessPal import complete: ${data.imported_count || 0} day(s)`, "success");
      await loadSyncedData();
      await loadWearableStatus();
    } catch (e) {
      toast(`Error importing MyFitnessPal CSV: ${e?.message || String(e)}`, "error");
    } finally {
      setSyncing(null);
      if (mfpFileRef.current) mfpFileRef.current.value = "";
    }
  };

  // ---------------- Mobile Health ----------------
  const syncMobileHealth = async (days = mobileSyncDays) => {
    setSyncing("mobile");
    try {
      if (!Capacitor.isNativePlatform()) {
        toast("Mobile health sync only works in the installed mobile app (Android/iOS).", "error");
        return;
      }

      if (!HealthSync?.getDailySummary) {
        toast(
          "HealthSync plugin is not available in this build. Check plugin registration, cap sync, and native rebuild.",
          "error"
        );
        return;
      }

      const payload = await HealthSync.getDailySummary({ days });

      const res = await fetch(`${BACKEND_URL}/api/wearables/mobile/sync`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Mobile sync failed");
      }

      const out = await res.json();
      toast(`Synced ${out.days_upserted || 0} day(s) from mobile health`, "success");

      await loadSyncedData();
      await loadWearableStatus();
    } catch (error) {
      console.error("[MobileSync] error:", error);
      toast(`Error syncing mobile health: ${error?.message || String(error)}`, "error");
    } finally {
      setSyncing(null);
    }
  };

  // ---------------- Manual import ----------------
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);

    try {
      if (importSource === "garmin") {
        await importGarminCSV(file);
        return;
      }
      if (importSource === "myfitnesspal") {
        await importMfpCSV(file);
        return;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("source", importSource);

      const response = await fetch(`${BACKEND_URL}/api/wearables/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast(`Successfully imported ${data.imported_count} records!`, "success");
        await loadSyncedData();
        await loadWearableStatus();
      } else {
        toast(data.detail || "Import failed", "error");
      }
    } catch (error) {
      toast(`Error importing: ${error?.message || String(error)}`, "error");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const getSourceColor = (source) => {
    switch (source) {
      case "fitbit":
        return "#00B0B9";
      case "oura":
        return "#23233D";
      case "withings":
        return "#111827";
      case "health_connect":
        return "#0ea5e9";
      case "apple_watch":
        return "#000";
      case "samsung_watch":
        return "#1428A0";
      case "healthkit":
        return "#000";
      case "samsung_health":
        return "#1428A0";
      case "garmin":
        return "#2563eb";
      case "myfitnesspal":
        return "#f97316";
      default:
        return "#666";
    }
  };

  const fmtKm = (v) => {
    if (v == null || v === "") return "-";
    const n = Number(v);
    if (!Number.isFinite(n)) return "-";
    const digits = n < 10 ? 2 : 1;
    return `${n.toFixed(digits)} km`;
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: "center", padding: "48px" }}>
          <p>Loading wearable devices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container" data-testid="wearables-view">
      <h1 className="heading-2" style={{ marginBottom: "8px" }}>
        Connected Devices
      </h1>
      <p className="body-medium" style={{ color: "var(--text-secondary)", marginBottom: "32px" }}>
        Sync your fitness data from wearable devices
      </p>

      {/* Mobile Health */}
      <div className="card" style={{ padding: "24px", marginBottom: "20px" }}>
        <h3 className="heading-4" style={{ marginBottom: "10px" }}>
          📱 Mobile Health (Health Connect / HealthKit)
        </h3>
        <p className="body-small" style={{ color: "var(--text-secondary)", marginBottom: "12px" }}>
          Available in the mobile app. Reads local health data and pushes it into your HealthTrail account.
          <br />
          <strong>Includes:</strong> steps, calories burned, distance, and (optionally) heart rate / weight when enabled in your Health Connect permissions.
        </p>

        <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          {[7, 14, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              className={mobileSyncDays === d ? "btn-primary" : "btn-secondary"}
              style={{ padding: "8px 12px", fontSize: 12, minWidth: 90 }}
              onClick={() => setMobileSyncDays(d)}
              disabled={syncing === "mobile"}
            >
              {d} Days
            </button>
          ))}
        </div>

        <button
          onClick={() => syncMobileHealth(mobileSyncDays)}
          disabled={syncing === "mobile"}
          className="btn-primary"
          style={{ width: "100%" }}
        >
          {syncing === "mobile" ? "Syncing…" : `🔄 Sync Mobile (${mobileSyncDays} days)`}
        </button>

        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <input
            type="checkbox"
            checked={autoMobileSyncEnabled}
            onChange={(e) => setAutoMobileSyncEnabled(e.target.checked)}
            disabled={!Capacitor.isNativePlatform()}
          />
          <span className="body-small" style={{ color: "var(--text-secondary)" }}>
            Auto-sync while app is open (every 6 hours)
          </span>
        </div>

        <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 8 }}>
          Note: background auto-sync when the app is closed requires native background scheduling (Android WorkManager / iOS background fetch).
        </div>
      </div>

      {/* Device Cards Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "20px",
          marginBottom: "24px",
        }}
      >
        {/* Fitbit */}
        <div className="card" style={{ padding: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
            <div
              style={{
                width: "56px",
                height: "56px",
                background: "linear-gradient(135deg, #00B0B9 0%, #00838A 100%)",
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontWeight: "bold",
                fontSize: "18px",
                boxShadow: "0 4px 12px rgba(0, 176, 185, 0.3)",
              }}
            >
              F
            </div>
            <div style={{ flex: 1 }}>
              <h3 className="heading-4" style={{ marginBottom: "4px" }}>Fitbit</h3>
              <p className="body-small" style={{ color: wearableStatus?.fitbit_connected ? "#10b981" : "var(--text-secondary)" }}>
                {wearableStatus?.fitbit_connected ? "✓ Connected" : "Not connected"}
              </p>
            </div>
          </div>

          {wearableStatus?.fitbit_connected ? (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <input
                  type="checkbox"
                  checked={!!wearableStatus?.auto_sync_fitbit}
                  onChange={(e) => setAutoSync("fitbit", e.target.checked)}
                />
                <span className="body-small" style={{ color: "var(--text-secondary)" }}>
                  Auto-sync Fitbit
                </span>
              </div>

              <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
                <button
                  onClick={syncFitbitData}
                  disabled={syncing === "fitbit" || syncing === "fitbit-history"}
                  className="btn-primary"
                  style={{ flex: 1 }}
                  type="button"
                >
                  {syncing === "fitbit" ? "Syncing..." : "🔄 Sync Today"}
                </button>
                <button onClick={disconnectFitbit} className="btn-secondary" style={{ color: "#dc2626" }} type="button">
                  Disconnect
                </button>
              </div>

              <div style={{ display: "flex", gap: "8px" }}>
                {[7, 30, 90].map((d) => (
                  <button
                    key={d}
                    onClick={() => syncFitbitHistory(d)}
                    disabled={syncing === "fitbit" || syncing === "fitbit-history"}
                    className="btn-secondary"
                    style={{ flex: 1, fontSize: "12px", padding: "8px" }}
                    type="button"
                  >
                    {syncing === "fitbit-history" ? "..." : `${d} Days`}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <button
              onClick={connectFitbit}
              className="btn-primary"
              style={{ width: "100%" }}
              disabled={!wearableStatus?.fitbit_configured}
              type="button"
            >
              {wearableStatus?.fitbit_configured ? "Connect Fitbit" : "Not Configured"}
            </button>
          )}

          <p className="body-small" style={{ color: "var(--text-muted)", marginTop: "12px", fontSize: "12px" }}>
            Steps, calories, heart rate, sleep, and activity data
          </p>
        </div>

        {/* Oura */}
        <div className="card" style={{ padding: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
            <div
              style={{
                width: "56px",
                height: "56px",
                background: "linear-gradient(135deg, #23233D 0%, #4A4A6A 100%)",
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(35, 35, 61, 0.3)",
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="9" stroke="white" strokeWidth="2" />
                <circle cx="12" cy="12" r="5" stroke="white" strokeWidth="2" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <h3 className="heading-4" style={{ marginBottom: "4px" }}>Oura Ring</h3>
              <p className="body-small" style={{ color: wearableStatus?.oura_connected ? "#10b981" : "var(--text-secondary)" }}>
                {wearableStatus?.oura_connected ? "✓ Connected" : "Not connected"}
              </p>
            </div>
          </div>

          {wearableStatus?.oura_connected ? (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <input
                  type="checkbox"
                  checked={!!wearableStatus?.auto_sync_oura}
                  onChange={(e) => setAutoSync("oura", e.target.checked)}
                />
                <span className="body-small" style={{ color: "var(--text-secondary)" }}>
                  Auto-sync Oura
                </span>
              </div>

              <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
                <button
                  onClick={syncOuraData}
                  disabled={syncing === "oura" || syncing === "oura-history"}
                  className="btn-primary"
                  style={{ flex: 1 }}
                  type="button"
                >
                  {syncing === "oura" ? "Syncing..." : "🔄 Sync Today"}
                </button>
                <button onClick={disconnectOura} className="btn-secondary" style={{ color: "#dc2626" }} type="button">
                  Disconnect
                </button>
              </div>

              <div style={{ display: "flex", gap: "8px" }}>
                {[7, 30, 90].map((d) => (
                  <button
                    key={d}
                    onClick={() => syncOuraHistory(d)}
                    disabled={syncing === "oura" || syncing === "oura-history"}
                    className="btn-secondary"
                    style={{ flex: 1, fontSize: "12px", padding: "8px" }}
                    type="button"
                  >
                    {syncing === "oura-history" ? "..." : `${d} Days`}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <button
              onClick={connectOura}
              className="btn-primary"
              style={{ width: "100%" }}
              disabled={!wearableStatus?.oura_configured}
              type="button"
            >
              {wearableStatus?.oura_configured ? "Connect Oura Ring" : "Not Configured"}
            </button>
          )}

          <p className="body-small" style={{ color: "var(--text-muted)", marginTop: "12px", fontSize: "12px" }}>
            Sleep, readiness, activity scores, HRV, and heart rate
          </p>
        </div>

        {/* Withings */}
        <div className="card" style={{ padding: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
            <div
              style={{
                width: "56px",
                height: "56px",
                background: "linear-gradient(135deg, #111827 0%, #374151 100%)",
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontWeight: "bold",
                fontSize: "18px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
              }}
            >
              W
            </div>
            <div style={{ flex: 1 }}>
              <h3 className="heading-4" style={{ marginBottom: "4px" }}>Withings</h3>
              <p className="body-small" style={{ color: wearableStatus?.withings_connected ? "#10b981" : "var(--text-secondary)" }}>
                {wearableStatus?.withings_connected ? "✓ Connected" : "Not connected"}
              </p>
            </div>
          </div>

          {wearableStatus?.withings_connected ? (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <input
                  type="checkbox"
                  checked={!!wearableStatus?.auto_sync_withings}
                  onChange={(e) => setAutoSync("withings", e.target.checked)}
                />
                <span className="body-small" style={{ color: "var(--text-secondary)" }}>
                  Auto-sync Withings
                </span>
              </div>

              <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                {[7, 14, 30, 90].map((d) => (
                  <button
                    key={d}
                    type="button"
                    className={withingsSyncDays === d ? "btn-primary" : "btn-secondary"}
                    style={{ padding: "8px 12px", fontSize: 12, minWidth: 90 }}
                    onClick={() => setWithingsSyncDays(d)}
                    disabled={syncing === "withings" || syncing === "withings-history"}
                  >
                    {d} Days
                  </button>
                ))}
              </div>

              <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
                <button
                  onClick={() => syncWithings(withingsSyncDays)}
                  disabled={syncing === "withings" || syncing === "withings-history"}
                  className="btn-primary"
                  style={{ flex: 1 }}
                  type="button"
                >
                  {syncing === "withings" || syncing === "withings-history"
                    ? "Syncing..."
                    : `🔄 Sync (${withingsSyncDays} days)`}
                </button>

                <button onClick={disconnectWithings} className="btn-secondary" style={{ color: "#dc2626" }} type="button">
                  Disconnect
                </button>
              </div>

              {wearableStatus?.last_withings_sync && (
                <div className="body-small" style={{ color: "var(--text-muted)" }}>
                  Last Withings sync: {new Date(wearableStatus.last_withings_sync).toLocaleString()}
                </div>
              )}
            </div>
          ) : (
            <button onClick={connectWithings} className="btn-primary" style={{ width: "100%" }} type="button">
              Connect Withings
            </button>
          )}

          <p className="body-small" style={{ color: "var(--text-muted)", marginTop: "12px", fontSize: "12px" }}>
            Weight and body metrics from Withings scales
          </p>
        </div>

        {/* Garmin */}
        <div className="card" style={{ padding: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
            <div
              style={{
                width: "56px",
                height: "56px",
                background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontWeight: "bold",
                fontSize: "18px",
                boxShadow: "0 4px 12px rgba(37, 99, 235, 0.28)",
              }}
            >
              G
            </div>
            <div style={{ flex: 1 }}>
              <h3 className="heading-4" style={{ marginBottom: "4px" }}>Garmin</h3>
              <p className="body-small" style={{ color: wearableStatus?.garmin_connected ? "#10b981" : "var(--text-secondary)" }}>
                {wearableStatus?.garmin_connected ? "✓ Connected" : "Not connected"}
              </p>
            </div>
          </div>

          {wearableStatus?.garmin_connected ? (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <input
                  type="checkbox"
                  checked={!!wearableStatus?.auto_sync_garmin}
                  onChange={(e) => setAutoSync("garmin", e.target.checked)}
                />
                <span className="body-small" style={{ color: "var(--text-secondary)" }}>
                  Auto-sync Garmin
                </span>
              </div>

              <div style={{ display: "flex", gap: "10px", marginBottom: "10px", flexWrap: "wrap" }}>
                <button
                  onClick={disconnectGarmin}
                  className="btn-secondary"
                  style={{ color: "#dc2626" }}
                  type="button"
                >
                  Disconnect
                </button>

                <label className="btn-primary" style={{ flex: 1, textAlign: "center", cursor: "pointer" }}>
                  {syncing === "garmin-import" ? "Importing…" : "📥 Import CSV"}
                  <input
                    type="file"
                    accept=".csv"
                    ref={garminFileRef}
                    onChange={(e) => importGarminCSV(e.target.files?.[0])}
                    style={{ display: "none" }}
                    disabled={syncing === "garmin-import"}
                  />
                </label>
              </div>
            </div>
          ) : (
            <button
              onClick={connectGarminManual}
              className="btn-primary"
              style={{ width: "100%" }}
              type="button"
            >
              Connect Garmin (Manual)
            </button>
          )}

          <p className="body-small" style={{ color: "var(--text-muted)", marginTop: "12px", fontSize: "12px" }}>
            Manual mode: import Garmin CSV exports (steps, calories burned, distance, active minutes, sleep)
          </p>
        </div>

        {/* MyFitnessPal */}
        <div className="card" style={{ padding: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
            <div
              style={{
                width: "56px",
                height: "56px",
                background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontWeight: "bold",
                fontSize: "18px",
                boxShadow: "0 4px 12px rgba(249, 115, 22, 0.28)",
              }}
            >
              MFP
            </div>
            <div style={{ flex: 1 }}>
              <h3 className="heading-4" style={{ marginBottom: "4px" }}>MyFitnessPal</h3>
              <p className="body-small" style={{ color: wearableStatus?.myfitnesspal_connected ? "#10b981" : "var(--text-secondary)" }}>
                {wearableStatus?.myfitnesspal_connected ? "✓ Connected" : "Not connected"}
              </p>
            </div>
          </div>

          {wearableStatus?.myfitnesspal_connected ? (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <input
                  type="checkbox"
                  checked={!!wearableStatus?.auto_sync_myfitnesspal}
                  onChange={(e) => setAutoSync("myfitnesspal", e.target.checked)}
                />
                <span className="body-small" style={{ color: "var(--text-secondary)" }}>
                  Auto-sync MyFitnessPal
                </span>
              </div>

              <div style={{ display: "flex", gap: "10px", marginBottom: "10px", flexWrap: "wrap" }}>
                <button
                  onClick={disconnectMfp}
                  className="btn-secondary"
                  style={{ color: "#dc2626" }}
                  type="button"
                >
                  Disconnect
                </button>

                <label className="btn-primary" style={{ flex: 1, textAlign: "center", cursor: "pointer" }}>
                  {syncing === "mfp-import" ? "Importing…" : "📥 Import CSV"}
                  <input
                    type="file"
                    accept=".csv"
                    ref={mfpFileRef}
                    onChange={(e) => importMfpCSV(e.target.files?.[0])}
                    style={{ display: "none" }}
                    disabled={syncing === "mfp-import"}
                  />
                </label>
              </div>
            </div>
          ) : (
            <button
              onClick={connectMfp}
              className="btn-primary"
              style={{ width: "100%" }}
              type="button"
            >
              {wearableStatus?.myfitnesspal_oauth_ready ? "Connect MyFitnessPal" : "Connect MyFitnessPal (Manual)"}
            </button>
          )}

          <p className="body-small" style={{ color: "var(--text-muted)", marginTop: "12px", fontSize: "12px" }}>
            {wearableStatus?.myfitnesspal_oauth_ready
              ? "OAuth connect is enabled. CSV import still works as a manual fallback for nutrition history."
              : "Manual mode: import nutrition CSV exports (calories in, macros, water)"}
          </p>
        </div>
      </div>

      {/* Synced Activity Data (Fitbit/Oura/Mobile) */}
      {syncedData.length > 0 ? (
        <div className="card">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <h3 className="heading-4" style={{ margin: 0 }}>
              📊 Synced Activity Data
            </h3>
            <div style={{ display: "flex", gap: "8px" }}>
              {[7, 14, 30, 90, 365].map((days) => (
                <button
                  key={days}
                  onClick={() => setDateRange(days)}
                  className={dateRange === days ? "btn-primary" : "btn-secondary"}
                  style={{ padding: "6px 12px", fontSize: "12px", minWidth: "auto" }}
                  type="button"
                >
                  {days === 365 ? "1Y" : days === 90 ? "90D" : days === 30 ? "30D" : days === 14 ? "14D" : "7D"}
                </button>
              ))}
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--border-light)" }}>
                  <th style={{ textAlign: "left", padding: "12px 8px", fontWeight: 600 }}>Date</th>
                  <th style={{ textAlign: "left", padding: "12px 8px", fontWeight: 600 }}>Source</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>Weight</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>Steps</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>Distance</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>Calories</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>Calories In</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>Macros</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>Active Min</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>Heart Rate</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>Sleep</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>Scores</th>
                </tr>
              </thead>
              <tbody>
                {syncedData.map((data, index) => (
                  <tr key={index} style={{ borderBottom: "1px solid var(--border-light)" }}>
                    <td style={{ padding: "12px 8px" }}>{data.date}</td>
                    <td style={{ padding: "12px 8px" }}>
                      <span
                        style={{
                          background: getSourceColor(data.source),
                          color: "white",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          textTransform: "capitalize",
                        }}
                      >
                        {(data.source || "").replace("_", " ")}
                      </span>
                    </td>
                    <td style={{ textAlign: "right", padding: "12px 8px" }}>
                      {data.weight != null ? `${Number(data.weight).toFixed(1)} lb` : "-"}
                    </td>
                    <td style={{ textAlign: "right", padding: "12px 8px" }}>{data.steps?.toLocaleString() || "-"}</td>
                    <td style={{ textAlign: "right", padding: "12px 8px" }}>
                      {fmtKm(data.distance_km)}
                    </td>
                    <td style={{ textAlign: "right", padding: "12px 8px" }}>
                      {data.calories_burned?.toLocaleString() || "-"}
                    </td>
                    <td style={{ textAlign: "right", padding: "12px 8px" }}>
                      {data.calories_consumed != null ? Number(data.calories_consumed).toLocaleString() : "-"}
                    </td>
                    <td style={{ textAlign: "right", padding: "12px 8px" }}>
                      {data.protein_g != null || data.carbs_g != null || data.fat_g != null ? (
                        <span style={{ fontSize: 12 }}>
                          {data.protein_g != null ? `P ${Math.round(Number(data.protein_g))}g ` : ""}
                          {data.carbs_g != null ? `C ${Math.round(Number(data.carbs_g))}g ` : ""}
                          {data.fat_g != null ? `F ${Math.round(Number(data.fat_g))}g` : ""}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td style={{ textAlign: "right", padding: "12px 8px" }}>{data.active_minutes || "-"}</td>
                    <td style={{ textAlign: "right", padding: "12px 8px" }}>
                      {data.resting_heart_rate ? `${data.resting_heart_rate} bpm` : "-"}
                    </td>
                    <td style={{ textAlign: "right", padding: "12px 8px" }}>
                      {data.sleep_minutes ? `${Math.floor(data.sleep_minutes / 60)}h ${data.sleep_minutes % 60}m` : "-"}
                    </td>
                    <td style={{ textAlign: "right", padding: "12px 8px" }}>
                      {data.source === "oura" ? (
                        <span style={{ fontSize: "12px" }}>
                          {data.readiness_score && (
                            <span title="Readiness" style={{ marginRight: "8px" }}>
                              🎯{data.readiness_score}
                            </span>
                          )}
                          {data.sleep_score && (
                            <span title="Sleep" style={{ marginRight: "8px" }}>
                              😴{data.sleep_score}
                            </span>
                          )}
                          {data.activity_score && <span title="Activity">🏃{data.activity_score}</span>}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="card" style={{ textAlign: "center", padding: "48px" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>⌚</div>
          <p className="body-medium" style={{ color: "var(--text-secondary)" }}>
            No synced activity data yet. Connect a device or import data to get started!
          </p>
        </div>
      )}

      {/* Manual import */}
      <div className="card" style={{ marginTop: 24 }}>
        <h3 className="heading-4" style={{ marginBottom: 16 }}>📥 Manual Import</h3>
        <div className="form-row" style={{ marginBottom: 16 }}>
          <div className="form-group">
            <label className="body-medium">Device Source</label>
            <select
              value={importSource}
              onChange={(e) => setImportSource(e.target.value)}
              className="form-input"
              data-testid="import-source-select"
            >
              <option value="apple_watch">Apple Watch</option>
              <option value="samsung_watch">Samsung Watch</option>
              <option value="garmin">Garmin</option>
              <option value="myfitnesspal">MyFitnessPal</option>
              <option value="whoop">Whoop</option>
              <option value="other">Other Device</option>
            </select>
          </div>

          <div className="form-group">
            <label className="body-medium">CSV File</label>
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="form-input"
              disabled={importing}
              data-testid="import-file-input"
            />
          </div>
        </div>

        {importing && <p className="body-small" style={{ color: "var(--accent-color)" }}>Importing data...</p>}
      </div>
    </div>
  );
}

export default WearablesView;